import { useState, useCallback } from 'react';
import { useDocumentUpload } from '../hooks';
import { useDocumentsStore } from '../store';
import { DocumentCategory, UploadedDocument } from '../types';
import { validateFile, formatBytes, getCategoryLabel } from '../utils';
import { DOCUMENT_CATEGORIES } from '../constants';
import { Button } from '@/components/ui/Button';
import { Upload, File, X, AlertCircle } from 'lucide-react';
import { cn } from '@/utils/cn';

interface DocumentUploadModalProps {
  context: 'domain' | 'folder' | 'employee';
  contextUuid: string;
  contextName?: string;
}

export const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  context,
  contextUuid,
  contextName,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [category, setCategory] = useState<DocumentCategory>('autre');
  const [errors, setErrors] = useState<string[]>([]);

  const { closeUploadModal } = useDocumentsStore();
  const { mutate: uploadDocuments, isPending } = useDocumentUpload();

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleFiles = useCallback((fileList: FileList | null) => {
    if (!fileList) return;

    const newFiles: File[] = [];
    const newErrors: string[] = [];

    Array.from(fileList).forEach(file => {
      const validation = validateFile(file);
      if (validation.valid) {
        newFiles.push(file);
      } else {
        newErrors.push(`${file.name}: ${validation.error}`);
      }
    });

    setFiles(prev => [...prev, ...newFiles]);
    setErrors(prev => [...prev, ...newErrors]);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles]
  );

  const removeFile = useCallback((index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpload = useCallback(() => {
    const uploadedDocuments: UploadedDocument[] = files.map(file => ({
      file,
      name: file.name,
      category,
      tags: [],
      context,
      contextUuid,
    }));

    uploadDocuments(uploadedDocuments, {
      onSuccess: () => {
        setFiles([]);
        setErrors([]);
        closeUploadModal();
      },
    });
  }, [
    files,
    category,
    context,
    contextUuid,
    uploadDocuments,
    closeUploadModal,
  ]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6">
        <h2 className="mb-4 text-2xl font-bold">Ajouter des documents</h2>
        {contextName && (
          <p className="mb-4 text-sm text-neutral-600">
            Contexte: <span className="font-medium">{contextName}</span>
          </p>
        )}

        {/* Category Selection */}
        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-neutral-700">
            Catégorie
          </label>
          <select
            value={category}
            onChange={e => setCategory(e.target.value as DocumentCategory)}
            className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-xl border border-neutral-300 px-4 py-2 focus:ring-2"
          >
            {DOCUMENT_CATEGORIES.map(cat => (
              <option key={cat} value={cat}>
                {getCategoryLabel(cat)}
              </option>
            ))}
          </select>
        </div>

        {/* Drag & Drop Zone */}
        <div
          className={cn(
            'rounded-xl border-2 border-dashed p-12 text-center transition-colors',
            dragActive
              ? 'border-primary-500 bg-primary-50'
              : 'border-neutral-300'
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload className="mx-auto mb-4 h-12 w-12 text-neutral-400" />
          <p className="mb-2 text-lg font-medium text-neutral-700">
            Glissez vos fichiers ici
          </p>
          <p className="mb-4 text-sm text-neutral-500">
            ou cliquez pour sélectionner
          </p>
          <input
            type="file"
            multiple
            onChange={e => handleFiles(e.target.files)}
            className="hidden"
            id="file-upload"
          />
          <label htmlFor="file-upload">
            <Button variant="primary" onClick={e => e.preventDefault()}>
              Sélectionner des fichiers
            </Button>
          </label>
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div className="bg-error-50 mt-4 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="text-error-600 h-5 w-5" />
              <div className="flex-1">
                <p className="text-error-800 font-medium">
                  Erreurs de validation
                </p>
                <ul className="text-error-700 mt-2 list-disc pl-5 text-sm">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => setErrors([])}
                className="hover:bg-error-100 rounded-lg p-1"
              >
                <X className="text-error-600 h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Files List */}
        {files.length > 0 && (
          <div className="mt-4 space-y-2">
            {files.map((file, index) => (
              <div
                key={index}
                className="flex items-center gap-3 rounded-lg bg-neutral-50 p-3"
              >
                <File className="h-5 w-5 text-neutral-600" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{file.name}</p>
                  <p className="text-xs text-neutral-500">
                    {formatBytes(file.size)}
                  </p>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="rounded p-1 hover:bg-neutral-200"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <Button onClick={closeUploadModal} variant="secondary">
            Annuler
          </Button>
          <Button
            onClick={handleUpload}
            disabled={files.length === 0 || isPending}
            variant="primary"
          >
            {isPending
              ? 'Téléversement...'
              : `Téléverser ${files.length} fichier(s)`}
          </Button>
        </div>
      </div>
    </div>
  );
};
